<template>
  <AuthCard :settings="settings">
    <Form :action="preview ? null : url" :data="data" @error="reloadCaptcha">
      <div v-if="message" class="alert alert-danger mb-4">
        {{ message }}
      </div>

      <input v-if="redirectedFrom" type="hidden" name="redirect" :value="redirectedFrom" />

      <template v-if="internalAuths || isLoginAs">
        <div class="identifier">
          <FormControl
              v-if="!isLoginAs"
              v-model="identifier"
              name="identifier"
              input-group-class="input-group-lg"
              :label="internalAuths.placeholder"
              :placeholder="`${internalAuths.placeholder}...`"
          />

          <FormControl
              v-else
              v-model="identifier"
              name="email"
              input-group-class="input-group-lg"
          />
        </div>

        <div class="password">
          <div class="password-label">
            <label for="password" class="form-label">
              {{ $t('validation.attributes.password') }}
            </label>

            <a v-if="!isLoginAs"
               :href="makeLink('/auth/password-reminder')"
               @click.prevent="preview ? null : router.push({ name: 'auth.password-reminder'})"
               class="link ms-auto fw-medium"
            >
              {{ $t('front.cant_sign_in') }}
            </a>
          </div>

          <FormControl
              label-disabled
              type="password"
              name="password"
              input-group-class="input-group-lg"
              :clearable="false"
          />
        </div>


        <Captcha ref="captchaRef" v-if="!isLoginAs && captcha" />

        <div v-if="!isLoginAs" class="remember-me mt-4" :class="{'remember-me--captcha': captcha}">
          <FormCheckbox name="remember_me" />
        </div>

        <Button type="submit" :disabled="!identifier" class="btn-primary btn-lg btn-rounded submit">
          {{ $t('global.continue') }}
        </Button>
      </template>

      <template v-if="!isLoginAs">
        <div v-if="appearance?.external_authentications?.length"
             class="external-auth"
        >
          <h4>{{ $t('front.external_login') }}</h4>

          <div class="external-auth__methods">
            <a v-for="method in appearance.external_authentications"
               :href="method.url"
            >
              <img class="img-fluid img-thumbnail"
                   :src="method.image"
                   alt="Microsoft">
            </a>
          </div>
        </div>

        <div class="registration" v-if="registrationEnabled">
          <span>{{ $t('front.dont_have_an_account')}}</span>
          <a class="link fw-medium" :href="makeLink('/auth/registration')">
            {{ $t('front.get_started')}}
          </a>
        </div>
      </template>

      <template v-if="!isLoginAs && (appearance.google_play_link || appearance.apple_store_link)">
        <div class="apps">
          <hr />

          <div class="apps__links">
            <a v-if="appearance.google_play_link"
               :href="appearance.google_play_link"
               target="_blank"
               class="apps-link apps-link--google-play"
            >
              <img :src="appearance.google_play_image" />
            </a>

            <a v-if="appearance.apple_store_link"
               :href="appearance.apple_store_link"
               target="_blank"
               class="apps-link apps-link--apple-store"
            >
              <img :src="appearance.apple_store_image" />
            </a>
          </div>
        </div>
      </template>
    </Form>
  </AuthCard>
</template>
<script setup>
import FormControl from "@/js/Elements/Form/FormControl.vue";
import Button from "@/js/Elements/Button.vue";
import FormCheckbox from "@/js/Elements/Form/FormCheckbox.vue";
import Form from "@/js/Elements/Form/Form.vue";
import {useRouter, useRoute } from "vue-router";
import AuthCard from "@/js/Views/Auth/AuthCard.vue";
import {computed, reactive, ref} from "vue";
import {makeLink} from "@/js/Plugins/Http.js";
import Captcha from "@/js/Elements/Form/Captcha.vue";
const router = useRouter();
const route = useRoute();

const props = defineProps({
  settings: Object,
  preview: Boolean,
});
const appearance = computed(() => props.settings || window.setup.login);
const captcha = computed(() => window.setup.login?.captcha);
const registrationEnabled = window.setup?.login?.allow_users_registration;
const isLoginAs = computed(() => route.name === 'login-as.form');
const url = computed(() => !isLoginAs.value ? '/authentication/store' : '/kjadiagdiogbpost');
const internalAuths = computed(() => {
  return appearance.value?.internal_auths;
});
const redirectedFrom = computed(() => {
  return route?.redirectedFrom?.fullPath;
});
const data = reactive({
  remember_me: 1,
});
const identifier = ref();
const message = computed(() => route?.query?.message);
const captchaRef = ref();


function reloadCaptcha() {
  if (!captchaRef.value) return;
  captchaRef.value.reload();
}
</script>
<style lang="scss" scoped>
@import "@/css/functions";
@import "@/css/variables";

.identifier {

}

:deep(.form-error) {
  padding-left: 0;
  padding-right: 0;
  margin-bottom: spacing(4);
}

:deep(.form-label) {
  font-size: fontSize(3);
  margin-bottom: spacing(1);
}

.link {
  text-decoration: none;
}

.password {
  display: flex;
  flex-direction: column;
  margin-top: spacing(5);

  &-label {
    display: flex;
    justify-content: space-around;
  }
}

.remember-me {
  margin-top: spacing(3);

  &--captcha {
    margin-top: spacing(1);
  }

  :deep(.form-check-label) {
    font-size: fontSize(4);
  }
}

.submit {
  width: 100%;
  margin-top: spacing(4);
}

.external-auth {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: spacing(5);

  &__methods {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: spacing(2);

    a {
      width: toRem(75);
      aspect-ratio: 1/1;
      overflow: hidden;
      border: themeColor('border') solid toRem(1);
      border-radius: $border-radius-lg;
      padding: spacing(1);

      img {
        width: 100%;
        height: 100%;
        object-fit: scale-down;
      }
    }
  }
}

.registration {
  display: flex;
  justify-content: center;
  gap: spacing(1);
  margin-top: spacing(5);
}

.apps {
  display: flex;
  flex-direction: column;

  hr {
    border-color: themeColor('neutral-600');
    margin: spacing(7) 0;
  }

  &__links {
    display: flex;
    justify-content: center;
    gap: spacing(3);
    width: 100%;
  }

  &-link {
    max-width: toRem(140);

    img {
      width: 100%;
      object-fit: scale-down;
      object-position: center;
    }
  }
}
</style>
